import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DownloadSimple,
  Eye,
  FileText,
  SpinnerGap,
  X
} from '@phosphor-icons/react';
import {
  downloadTaskDocument,
  fetchTaskDocumentBlob
} from '../../../services/tasks/taskDocumentsService.js';

const IMAGE_EXTENSIONS = new Set(['PNG', 'JPG', 'JPEG', 'WEBP', 'GIF', 'BMP', 'SVG']);
const TEXT_EXTENSIONS = new Set(['TXT', 'MD', 'CSV', 'JSON', 'XML', 'HTML', 'LOG']);

const resolvePreviewType = (document, blob) => {
  const mimeType = String(blob?.type || document?.mimeType || '').toLowerCase();
  const extension = String(document?.extension || '').toUpperCase();

  if (mimeType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }

  if (mimeType.includes('pdf') || extension === 'PDF') {
    return 'pdf';
  }

  if (mimeType.startsWith('text/') || TEXT_EXTENSIONS.has(extension)) {
    return 'text';
  }

  return 'unsupported';
};

function DocumentPreviewModal({ isOpen, document, taskId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewState, setPreviewState] = useState({
    type: '',
    url: '',
    text: ''
  });
  const [downloading, setDownloading] = useState(false);

  const modalTarget = useMemo(() => (
    typeof window !== 'undefined' ? window.document.body : null
  ), []);

  useEffect(() => {
    if (!isOpen || !document || !taskId) {
      return undefined;
    }

    let cancelled = false;
    let objectUrlToCleanup = '';

    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      setPreviewState({
        type: '',
        url: '',
        text: ''
      });

      try {
        const { blob } = await fetchTaskDocumentBlob(taskId, document);
        if (cancelled) {
          return;
        }

        const previewType = resolvePreviewType(document, blob);

        if (previewType === 'text') {
          const text = await blob.text();
          if (cancelled) {
            return;
          }

          setPreviewState({
            type: 'text',
            url: '',
            text
          });
          return;
        }

        if (previewType === 'image' || previewType === 'pdf') {
          objectUrlToCleanup = window.URL.createObjectURL(blob);
          setPreviewState({
            type: previewType,
            url: objectUrlToCleanup,
            text: ''
          });
          return;
        }

        setPreviewState({
          type: 'unsupported',
          url: '',
          text: ''
        });
      } catch (previewError) {
        if (cancelled) {
          return;
        }

        setError(previewError.message || 'No se pudo cargar la vista previa del documento.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;

      if (objectUrlToCleanup) {
        window.URL.revokeObjectURL(objectUrlToCleanup);
      }
    };
  }, [document, isOpen, taskId]);

  useEffect(() => {
    if (!isOpen) {
      setPreviewState({
        type: '',
        url: '',
        text: ''
      });
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.document.addEventListener('keydown', handleEscape);
    return () => window.document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !document || !modalTarget) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);

    try {
      const { blob, fileName } = await downloadTaskDocument(taskId, document);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');

      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10010] bg-black/70 p-4">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative mx-auto flex h-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-line bg-bg shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-accent-text">
              <Eye size={14} />
              Vista previa
            </div>
            <h3 className="mt-1 truncate text-lg font-semibold text-content">{document.name}</h3>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 rounded-xl border border-amber/40 bg-[--app-sidebar-active] px-4 py-2 text-sm font-medium text-accent-text transition-colors hover:bg-amber/20 hover:text-amber-hover disabled:cursor-not-allowed disabled:opacity-70"
            >
              {downloading ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
              {downloading ? 'Descargando...' : 'Descargar'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-surface text-content transition-colors hover:bg-surface-2 hover:text-content"
              aria-label="Cerrar vista previa"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-bg/60 p-5">
          {loading && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="inline-flex items-center gap-3 rounded-2xl border border-line bg-bg/70 px-5 py-4 text-sm font-medium text-content">
                <SpinnerGap size={18} className="animate-spin" />
                Cargando vista previa...
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="max-w-xl rounded-2xl border border-red-800/70 bg-red-950/20 p-6 text-center">
                <p className="text-base font-semibold text-red-200">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && previewState.type === 'image' && (
            <div className="flex min-h-[320px] items-center justify-center">
              <img
                src={previewState.url}
                alt={document.name}
                className="max-h-[75vh] max-w-full rounded-2xl border border-line object-contain"
              />
            </div>
          )}

          {!loading && !error && previewState.type === 'pdf' && (
            <div className="h-full min-h-[70vh] overflow-hidden rounded-2xl border border-line bg-white">
              <iframe
                title={`Vista previa de ${document.name}`}
                src={previewState.url}
                className="h-[70vh] w-full"
              />
            </div>
          )}

          {!loading && !error && previewState.type === 'text' && (
            <div className="min-h-[320px] overflow-auto rounded-2xl border border-line bg-bg/70 p-5">
              <pre className="whitespace-pre-wrap break-words font-mono text-sm text-content">
                {previewState.text}
              </pre>
            </div>
          )}

          {!loading && !error && previewState.type === 'unsupported' && (
            <div className="flex h-full min-h-[320px] items-center justify-center">
              <div className="max-w-xl rounded-2xl border border-line bg-bg/70 p-6 text-center">
                <FileText size={36} className="mx-auto text-content-muted" />
                <p className="mt-4 text-lg font-semibold text-content">Vista previa no disponible</p>
                <p className="mt-2 text-sm text-content-muted">
                  Este tipo de archivo no se puede previsualizar por ahora. Puede descargarlo desde aquí.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    modalTarget
  );
}

export default DocumentPreviewModal;
